import {
  blockedInFullstorySelector,
  inlineDisplays,
  keptTags,
  loadingIndicatorSelector,
  notReadableByOperatorSelector,
} from "./constants";
import { describeHref } from "./describeHref";
import { escapeText } from "./escapeText";
import { html } from "./html";
import { isElementVisible } from "./isElementVisible";
import { markScrapeLines, resolveScrapeMarkers, stripScrapeMarkers } from "./scrapeIds";
import type { HtmlAttributeValue, ScrapeNodeResult } from "./types";

/** The attributes Operator needs from a real page element. Described elements carry their own. */
const readAttributes = (element: HTMLElement): Record<string, HtmlAttributeValue> => {
  const attributes: Record<string, HtmlAttributeValue> = {
    id: element.getAttribute("data-operator-id") ?? undefined,
    "aria-label": element.getAttribute("aria-label") ?? undefined,
    disabled: "disabled" in element && element.disabled === true,
  };
  if (element instanceof HTMLAnchorElement) {
    attributes.href = describeHref(element);
  }
  if (element instanceof HTMLInputElement) {
    const isToggle = element.type === "checkbox" || element.type === "radio";
    attributes.type = element.type === "text" ? undefined : element.type;
    attributes.placeholder = element.placeholder;
    attributes.checked = element.checked;
    // A toggle's value is a form detail ("on" by default); its checked state is what the user sees.
    attributes.value = element.type === "password" || isToggle ? undefined : element.value.trim();
  }
  if (element instanceof HTMLTextAreaElement) {
    attributes.placeholder = element.placeholder;
  }
  if (element instanceof HTMLIFrameElement) {
    attributes.title = element.title || element.getAttribute("aria-label") || element.name || undefined;
    attributes["aria-label"] = undefined;
  }
  if (element instanceof HTMLImageElement) {
    attributes.alt = element.alt;
  }
  return attributes;
};

/** A described element sits on its own line; for anything else the browser's layout decides. */
const isBlock = (node: Node): boolean =>
  node instanceof HTMLElement &&
  (node.hasAttribute("data-operator-description") || !inlineDisplays.has(window.getComputedStyle(node).display));

/**
 * Serializes what the user sees in `root` as a stripped HTML clone: visible text, the tags in
 * `keptTags` with the attributes from `readAttributes`, and `data-operator-description` markup
 * spliced in verbatim in place of the element it describes. Layout wrappers disappear; a block
 * boundary becomes a newline and an inline boundary a space, as when copying text from a page.
 *
 * Descriptions are first-party HTML and are never re-parsed or escaped, so JSON payloads and
 * nonce-wrapped editor content inside them stay byte-exact. Page text is always escaped, so
 * nothing a user typed can read as markup. With `assignScrapeIds`, the private-use sentinel that
 * carries line ids is stripped from descriptions as well as from page text, so a payload cannot
 * forge a line id.
 *
 * With `assignScrapeIds`, every line of the output is claimed by the element that reads as its
 * unit — a kept tag whose content fits on one line owns that line, and otherwise the nearest block
 * owns the lines its children left unclaimed — and that element is given a stable
 * `data-operator-scrape-id`. The ids travel inside the content as inline markers (see
 * `./scrapeIds`) so a later read can report only what changed.
 */
export const scrapeNode = (
  root: Node | null,
  shouldSkipBlockedInFullstory: boolean,
  assignScrapeIds = false
): ScrapeNodeResult => {
  let loading = false;

  /** Elements that claimed a line, by claim token; only the claims that survive get an id. */
  const claimants: HTMLElement[] = [];
  const claimTokens = new Map<HTMLElement, string>();
  const claimTokenFor = (element: HTMLElement): string => {
    let token = claimTokens.get(element);
    if (token === undefined) {
      token = String(claimants.push(element) - 1);
      claimTokens.set(element, token);
    }
    return token;
  };

  /**
   * Claims the lines of `content` for `element`: a single-line element owns its whole line, even
   * the parts nested elements claimed first, since the line reads as one unit; otherwise only the
   * lines no nested element claimed. Nothing is claimed by an inline wrapper the browser does not
   * lay out on its own line, so its text falls to the nearest block.
   */
  const claimLines = (element: HTMLElement, content: string, isKept: boolean): string => {
    if (!assignScrapeIds || !content) {
      return content;
    }
    const getClaim = () => claimTokenFor(element);
    if (isKept && !content.includes("\n")) {
      return markScrapeLines(stripScrapeMarkers(content), getClaim);
    }
    if (isKept || element === root || isBlock(element)) {
      return markScrapeLines(content, getClaim);
    }
    return content;
  };

  /** Page text with any stray sentinel character removed, so it cannot corrupt an id marker. */
  const sanitize = (value: string): string => (assignScrapeIds ? stripScrapeMarkers(value) : value);

  /** `readAttributes`, with attribute values that came from the page sanitized like text. */
  const attributes = (element: HTMLElement): Record<string, HtmlAttributeValue> => {
    const read = readAttributes(element);
    if (!assignScrapeIds) {
      return read;
    }
    return Object.fromEntries(
      Object.entries(read).map(([name, value]) => [name, typeof value === "string" ? sanitize(value) : value])
    );
  };

  const scrapeChildren = (element: HTMLElement, preserveWhitespace: boolean): string => {
    if (preserveWhitespace) {
      return Array.from(element.childNodes)
        .map((child) => (child instanceof HTMLBRElement ? "\n" : scrape(child, true)))
        .join("");
    }

    let text = "";
    let previousWasBlock = false;
    for (const child of element.childNodes) {
      // A line break has no text of its own but is a block boundary for its neighbours.
      if (child instanceof HTMLBRElement) {
        previousWasBlock = true;
        continue;
      }
      const childText = scrape(child, false);
      if (!childText) {
        continue;
      }
      const block = isBlock(child);
      if (text) {
        text += block || previousWasBlock ? "\n" : " ";
      }
      text += childText;
      previousWasBlock = block;
    }
    return text;
  };

  /** The element's own serialization, before any line of it is claimed for an id. */
  const scrapeElement = (node: HTMLElement, tag: string, preserveWhitespace: boolean): string => {
    if (node.hasAttribute("data-operator-redacted")) {
      // Only the tag survives: an href, placeholder or checked state would be the value by another name.
      return html(keptTags.has(tag) ? tag : "span", { "data-operator-redacted": true });
    }
    if (node.hasAttribute("data-operator-description")) {
      // Strip sentinels before claimLines. A multi-line description is joined with children that
      // already carry real markers, so it cannot be stripped again later without losing those.
      const description = sanitize(node.getAttribute("data-operator-description") || "");
      if (!node.hasAttribute("data-operator-include-children")) {
        return description;
      }
      return [description, scrapeChildren(node, preserveWhitespace)].filter(Boolean).join("\n");
    }
    if (node instanceof HTMLTextAreaElement) {
      return html(tag, attributes(node), sanitize(escapeText(node.value.trim())));
    }
    if (node instanceof HTMLSelectElement) {
      const selected = node.options[node.selectedIndex];
      return html(
        tag,
        attributes(node),
        selected ? html("option", { selected: true }, sanitize(escapeText(selected.text.trim()))) : ""
      );
    }
    if (node instanceof HTMLInputElement || node instanceof HTMLImageElement || node instanceof HTMLIFrameElement) {
      return html(tag, attributes(node));
    }

    const children = scrapeChildren(node, preserveWhitespace || tag === "pre");
    if (!keptTags.has(tag)) {
      return children || sanitize(escapeText(node.getAttribute("aria-label") || ""));
    }
    return html(tag, attributes(node), children);
  };

  /** `preserveWhitespace` is set inside `<pre>`, where newlines and indentation are content. */
  const scrape = (node: Node | null, preserveWhitespace: boolean): string => {
    if (node instanceof Text) {
      const content = node.textContent || "";
      return sanitize(escapeText(preserveWhitespace ? content : content.replace(/\s+/g, " ").trim()));
    }
    if (!(node instanceof HTMLElement) || !isElementVisible(node)) {
      return "";
    }
    if (node.matches(notReadableByOperatorSelector)) {
      return "";
    }
    if (shouldSkipBlockedInFullstory && node.matches(blockedInFullstorySelector)) {
      return "";
    }
    loading ||= node.matches(loadingIndicatorSelector);

    const tag = node.tagName.toLowerCase();
    // A described or redacted element is reported as a unit, like a kept tag.
    const isKept =
      keptTags.has(tag) ||
      node.hasAttribute("data-operator-description") ||
      node.hasAttribute("data-operator-redacted");

    return claimLines(node, scrapeElement(node, tag, preserveWhitespace), isKept);
  };

  const content = scrape(root, false);
  return { content: assignScrapeIds ? resolveScrapeMarkers(content, claimants) : content, loading };
};
